import { debounce } from "lodash";
import { useListFilterContext } from "ra-core";
import { FC, createContext, useContext, useMemo, useState } from "react";
import { RoleResponseDto, UserInvitationSearchCriterias } from "@/gen/api/user";


interface UserInvitationsTableSearchContextProps {
  states: {
    email: string;
    roles: RoleResponseDto[];
  },
  searchEmail: (s: string) => void;
  setSearchRole: (role: RoleResponseDto, remove: boolean) => void;
}

const UserInvitationsTableSearchContext = createContext<UserInvitationsTableSearchContextProps>({} as any)

const UserInvitationsTableSearchProvider: FC = ({ children }) => {
  const { setFilters, displayedFilters } = useListFilterContext()

  const [states, setStates] = useState({
    email: '',
    roles: [] as RoleResponseDto[],
  })

  const ctrls = useMemo(() => {
    const invokeSearch = (s1: typeof states) => {
      const input : UserInvitationSearchCriterias = {
        email: s1.email || null,
        customRoleIds: s1.roles.filter(r => !r.isDefault).map(r => r.roleId!),
        defaultRoleIds: s1.roles.filter(r => r.isDefault).map(r => r.roleId!),
      }
      setFilters(input, displayedFilters)
    }
    
    const debouncedInvokeSearch = debounce(invokeSearch, 300)

    return {
      searchEmail: (s: string) => {
        setStates(v => {
          if (v.email !== s) {
            const v1 = {...v, email: s}
            setTimeout(() => debouncedInvokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },
      setSearchRole: (role: RoleResponseDto, remove: boolean) => {
        setStates(v => {
          const i = v.roles.findIndex(r => r.roleId === role.roleId && r.organizationId === role.organizationId)
          if (remove && i > -1) {
            const roles = [...v.roles]
            roles.splice(i, 1)
            const v1 = {...v, roles}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else if (!remove && i === -1) {
            v.roles.push(role)
            const v1 = {...v}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },
    }
  }, [displayedFilters, setFilters])

  return (
    <UserInvitationsTableSearchContext.Provider value={{...ctrls, states}}>
      {children}
    </UserInvitationsTableSearchContext.Provider>
  )
}

export default UserInvitationsTableSearchProvider;

export const useUserInvitationsTableSearchContext = () => useContext(UserInvitationsTableSearchContext)
